import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";
import { apiClient } from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Initial Load
  useEffect(() => {
    if (token) {
      loadProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadProfile = async () => {
    console.log("[Auth] me start");
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      // Use centralized client (includes X-Tenant-Id from storage automatically)
      const response = await apiClient.get('/auth/me', {
        signal: controller.signal
      });

      console.log("[Auth] me success");
      // The backend /auth/me returns just User object usually, 
      // but we need { user, tenant, entitlements } if backend supports it.
      // Based on previous file, it seemed to support it. 
      // If server.py get_current_user_info just returns User, we might need separate calls.
      // Server code: returns current_user (User model).
      // We might need to fetch Tenant separately if not included.
      // Let's assume response.data is User, and we fetch Tenant from User info or separate call.

      const userData = response.data;

      // Fetch Tenant details explicitly if needed, or assume userData has it
      // Standard /auth/me in server.py returns simple User.
      // We will assume simpler structure for now and maybe fetch tenant.
      setUser(userData);

      // Hack/Optimisation: If user data has tenant info embedded?
      // For now, let's keep it safe. If we need full tenant object:
      if (userData.tenant_id) {
        try {
          // Robust Profile: Fetch Organization details from the correct endpoint
          const tenRes = await apiClient.get('/organization');
          setTenant(tenRes.data);
        } catch (e) {
          console.warn("Could not fetch organization details", e);
          setTenant({ id: userData.tenant_id });
        }
      }

      // Entitlements? (Legacy)
      setEntitlements(null);

    } catch (error) {
      console.error("[Auth] me error", error);
      // Fallback Strategy: Decode token
      try {
        const decoded = jwtDecode(token);
        setUser({
          id: decoded.sub,
          role: decoded.role,
          tenant_id: decoded.tenant_id,
          email: decoded.email
        });
        setTenant({ id: decoded.tenant_id });
      } catch (decodeError) {
        logout();
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const login = async (email, password, tenantId) => {
    try {
      // Hardened Login: Require Tenant ID
      const headers = {};

      // 1. We no longer inject X-Tenant-Id for login, letting the backend resolve it based on email
      // unless an explicit non-auto tenantId was provided.
      if (tenantId && tenantId !== "auto") {
        headers['X-Tenant-Id'] = tenantId;
      }

      // Use apiClient (but explicitly set header to override or ensure set)
      const response = await apiClient.post('/auth/login', {
        email,
        password
      }, {
        headers,
        timeout: 10000
      });

      const { access_token, user: loggedUser, tenant: loggedTenant } = response.data;

      // SUCCESS: Persist everything
      localStorage.setItem('token', access_token);
      localStorage.setItem('tenant_id', loggedTenant.id); // Validated ID from backend

      setToken(access_token);
      setUser(loggedUser);
      setTenant(loggedTenant);

      return { success: true };

    } catch (error) {
      console.error('Login error:', error);

      const res = error.response;
      let errorMessage = 'Error al iniciar sesión';
      let debugInfo = null;

      if (res) {
        // A) Standard Detail
        if (res.data?.detail) errorMessage = res.data.detail;

        // B) Also extract new body-based debug fields if headers are stripped
        const failReason = res.headers['x-auth-fail-reason'] || res.data?.reason;
        const debugJson = res.headers['x-auth-debug'] || res.data?.debug;

        if (failReason) {
          // Force debug display via tricking the isDev var later or just assigning it
          debugInfo = { reason: failReason, raw: debugJson };
        }
      }

      return {
        success: false,
        error: errorMessage,
        debug: debugInfo
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenant_id'); // Clear tenant on logout to force re-selection? Or keep?
    // User requested "logout clears token + tenant_id" in Goal C
    setToken(null);
    setUser(null);
    setTenant(null);
    setEntitlements(null);
  };

  // Role Helpers
  const isSuperAdmin = () => user?.role === 'SUPER_ADMIN';
  const isAdmin = () => user?.role === 'ADMIN_DESPACHO' || user?.role === 'SUPER_ADMIN';
  const isOperador = () => user?.role === 'OPERADOR';
  const isCliente = () => user?.role === 'CLIENTE';

  // Tenant Helpers
  const isCompany = () => tenant?.tenant_type === 'COMPANY';
  const isLawFirm = () => !tenant?.tenant_type || tenant?.tenant_type === 'LAW_FIRM';
  const isEndUser = () => tenant?.tenant_type === 'END_USER';

  const can = () => true; // Simplified for now
  const limit = () => -1;

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        entitlements,
        token,
        loading,
        login,
        logout,
        isSuperAdmin, isOperador, isAdmin, isCliente,
        isCompany, isLawFirm, isEndUser,
        can, limit
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

